
import { useState, useCallback, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Event {
  id: string;
  title: string;
  start: string;
  end: string;
  type: string;
}

interface ContinuousBarCalendarProps {
  events: Event[];
  onDateSelect: (start: string, end: string) => void;
  onEventClick: (event: Event) => void;
  onEventMove: (eventId: string, start: string, end: string) => void;
}

export const ContinuousBarCalendar = ({ 
  events, 
  onDateSelect, 
  onEventClick, 
  onEventMove 
}: ContinuousBarCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    startDate: Date | null;
    endDate: Date | null;
  }>({
    isDragging: false,
    startDate: null,
    endDate: null
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get event type colors
  const getEventTypeColor = (type: string) => {
    const colors = {
      advertising: '#0EA5E9',
      pr: '#8B5CF6',
      promotion: '#F97316',
      event: '#22C55E',
      social: '#EC4899'
    };
    return colors[type as keyof typeof colors] || '#6B7280';
  };

  // Format date for tooltip display
  const formatDateForTooltip = useCallback((dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  // Get event type display name
  const getEventTypeDisplayName = (type: string) => {
    const typeNames = {
      advertising: 'Advertising',
      pr: 'Public Relations',
      promotion: 'Marketing Promotion',
      event: 'Event (online/offline)',
      social: 'Social Media'
    };
    return typeNames[type as keyof typeof typeNames] || type;
  };

  // Check if a date is in the past
  const isDateInPast = useCallback((date: Date) => {
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    return dateOnly < today;
  }, [today]);

  // Generate calendar weeks starting with Monday
  const generateCalendarWeeks = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Adjust to start on Monday (1) instead of Sunday (0)
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0 days, Sunday = 6 days
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    const endDate = new Date(lastDay);
    const lastDayOfWeek = lastDay.getDay();
    const daysToAdd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek; // Sunday = 0 days to add
    endDate.setDate(endDate.getDate() + daysToAdd);
    
    const weeks = [];
    let current = new Date(startDate);
    
    while (current <= endDate) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }
    
    return weeks;
  }, [currentMonth]);

  // Calculate event positions for continuous display
  const calculateEventPositions = useCallback((weekIndex: number, week: Date[]) => {
    const weekStart = week[0];
    const weekEnd = week[6];
    
    // Get events that overlap with this week
    const weekEvents = events.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      return eventStart <= weekEnd && eventEnd >= weekStart;
    });

    // Sort events by start date to maintain consistent layering
    weekEvents.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    return weekEvents.map((event, index) => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      // Calculate position within the week
      const startCol = Math.max(0, Math.floor((eventStart.getTime() - weekStart.getTime()) / (24 * 60 * 60 * 1000)));
      const endCol = Math.min(6, Math.floor((eventEnd.getTime() - weekStart.getTime()) / (24 * 60 * 60 * 1000)));
      
      const width = ((endCol - startCol + 1) / 7) * 100;
      const left = (startCol / 7) * 100;
      
      return {
        ...event,
        left: `${left}%`,
        width: `${width}%`,
        layer: index,
        startCol,
        endCol
      };
    });
  }, [events]);

  // Handle mouse events for date selection
  const handleMouseDown = useCallback((date: Date) => {
    if (isDateInPast(date)) return;
    
    setDragState({
      isDragging: true,
      startDate: date,
      endDate: date
    });
  }, [isDateInPast]);

  const handleMouseEnter = useCallback((date: Date) => {
    if (dragState.isDragging && dragState.startDate && !isDateInPast(date)) {
      setDragState(prev => ({
        ...prev,
        endDate: date
      }));
    }
  }, [dragState.isDragging, dragState.startDate, isDateInPast]);

  const handleMouseUp = useCallback(() => {
    if (dragState.isDragging && dragState.startDate && dragState.endDate) {
      const start = dragState.startDate < dragState.endDate ? dragState.startDate : dragState.endDate;
      const end = dragState.startDate > dragState.endDate ? dragState.startDate : dragState.endDate;
      
      // Only proceed if neither date is in the past
      if (!isDateInPast(start) && !isDateInPast(end)) {
        // Set start time to 8AM local time
        const startDateTime = new Date(start);
        startDateTime.setHours(8, 0, 0, 0);
        
        // Set end time to 6PM local time
        const endDateTime = new Date(end);
        endDateTime.setHours(18, 0, 0, 0);
        
        onDateSelect(
          startDateTime.toISOString(),
          endDateTime.toISOString()
        );
      }
    }
    setDragState({
      isDragging: false,
      startDate: null,
      endDate: null
    });
  }, [dragState, onDateSelect, isDateInPast]);

  // Check if date is in selection range
  const isDateInSelection = useCallback((date: Date) => {
    if (!dragState.startDate || !dragState.endDate) return false;
    const start = dragState.startDate < dragState.endDate ? dragState.startDate : dragState.endDate;
    const end = dragState.startDate > dragState.endDate ? dragState.startDate : dragState.endDate;
    return date >= start && date <= end;
  }, [dragState]);

  // Calculate week height based on number of events with increased minimum height
  const getWeekHeight = useCallback((weekIndex: number, week: Date[]) => {
    const weekEvents = calculateEventPositions(weekIndex, week);
    const minHeight = 120; // Increased minimum height
    const eventHeight = 16; // Reduced event height
    const eventSpacing = 3; // Reduced spacing
    const padding = 40; // Increased padding for better spacing
    
    return Math.max(minHeight, (weekEvents.length * (eventHeight + eventSpacing)) + padding);
  }, [calculateEventPositions]);

  // Handle event click with past date check
  const handleEventClick = useCallback((event: Event) => {
    const eventStart = new Date(event.start);
    if (!isDateInPast(eventStart)) {
      onEventClick(event);
    }
  }, [onEventClick, isDateInPast]);

  return (
    <TooltipProvider>
      <div className="bg-white rounded-lg p-4" onMouseUp={handleMouseUp}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
          </div>
          
          {/* Centered Legend */}
          <div className="flex justify-center flex-1">
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#0EA5E9' }}></div>
                <span className="text-xs text-gray-600">Advertising</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#8B5CF6' }}></div>
                <span className="text-xs text-gray-600">PR</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F97316' }}></div>
                <span className="text-xs text-gray-600">Promotion</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22C55E' }}></div>
                <span className="text-xs text-gray-600">Event</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EC4899' }}></div>
                <span className="text-xs text-gray-600">Social</span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date())}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Instructional Text */}
        <div className="text-center mb-2">
          <p className="text-[12px] text-black">
            Drag and select days, or click on a date on the calendar to add new activities
          </p>
        </div>

        {/* Calendar Header - Updated to start with Monday */}
        <div className="grid grid-cols-7 mb-2">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="p-2 text-center font-semibold text-gray-700 border-b">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Weeks */}
        <div className="space-y-1">
          {generateCalendarWeeks.map((week, weekIndex) => {
            const weekHeight = getWeekHeight(weekIndex, week);
            const weekEvents = calculateEventPositions(weekIndex, week);
            
            return (
              <div key={weekIndex} className="relative border border-gray-200" style={{ height: `${weekHeight}px` }}>
                {/* Day Grid */}
                <div className="grid grid-cols-7 h-full">
                  {week.map((date, dayIndex) => {
                    const isPast = isDateInPast(date);
                    return (
                      <div
                        key={dayIndex}
                        className={`border-r border-gray-200 p-1 ${
                          isPast 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'cursor-pointer hover:bg-gray-50'
                        } ${
                          isDateInSelection(date) && !isPast ? 'bg-blue-100' : ''
                        } ${
                          date.getMonth() !== currentMonth.getMonth() ? 'text-gray-400' : ''
                        }`}
                        onMouseDown={() => !isPast && handleMouseDown(date)}
                        onMouseEnter={() => !isPast && handleMouseEnter(date)}
                      >
                        <div className="text-sm font-medium">
                          {date.getDate()}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Event Bars Layer */}
                <div className="absolute inset-0 pointer-events-none">
                  {weekEvents.map((event) => {
                    const isPastEvent = isDateInPast(new Date(event.start));
                    return (
                      <Tooltip key={`${event.id}-${weekIndex}`}>
                        <TooltipTrigger asChild>
                          <div
                            className={`absolute pointer-events-auto rounded px-1 flex items-center justify-center text-white text-[10px] font-medium overflow-hidden ${
                              isPastEvent ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                            }`}
                            style={{
                              left: event.left,
                              width: event.width,
                              top: `${35 + (event.layer * 19)}px`, // Adjusted spacing for reduced height
                              height: '16px', // Reduced height
                              backgroundColor: getEventTypeColor(event.type),
                              zIndex: 10 + event.layer
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isPastEvent) {
                                handleEventClick(event);
                              }
                            }}
                          >
                            <span className="truncate text-ellipsis overflow-hidden whitespace-nowrap w-full text-center">
                              {event.title}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs bg-white border border-gray-200 text-gray-900 shadow-lg">
                          <div className="space-y-1">
                            <div className="font-semibold">{event.title}</div>
                            <div className="text-sm text-gray-600">
                              Type: {getEventTypeDisplayName(event.type)}
                            </div>
                            <div className="text-sm text-gray-600">
                              From: {formatDateForTooltip(event.start)}
                            </div>
                            <div className="text-sm text-gray-600">
                              To: {formatDateForTooltip(event.end)}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};
